import { useEffect, useState, useRef } from "react";
import { api, fileUrl } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Edit, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";

export default function EcommerceManager({ storeId }) {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get(`/products?store_id=${storeId}`).then(r => setProducts(r.data));
  useEffect(() => { if (storeId) load(); }, [storeId]);

  const del = async (id) => {
    if (!confirm("Delete product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted"); load();
  };

  if (!storeId) return <div className="text-center py-12 text-slate-400">No store linked</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold">Products ({products.length})</h3>
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-sky-500 hover:bg-sky-600 rounded-full" data-testid="add-product">
          <Plus className="w-4 h-4 mr-1.5"/>Add product
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map(p => (
          <Card key={p.product_id} className="p-4 flex gap-3" data-testid={`prod-${p.product_id}`}>
            <img src={fileUrl(p.image_url) || "https://placehold.co/120"} className="w-20 h-20 rounded-lg object-cover border" alt=""/>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{p.name}</div>
              <div className="text-lg font-black font-mono text-sky-600">৳{p.price}</div>
              <div className="flex gap-1 mt-2">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEditing(p); setOpen(true); }}><Edit className="w-3 h-3"/></Button>
                <Button size="sm" variant="outline" className="h-7 text-xs text-rose-600 border-rose-200" onClick={() => del(p.product_id)}><Trash2 className="w-3 h-3"/></Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
      <ProductDialog open={open} onOpenChange={setOpen} storeId={storeId} editing={editing} onSaved={() => { load(); setOpen(false); }}/>
    </div>
  );
}

function ProductDialog({ open, onOpenChange, storeId, editing, onSaved }) {
  const [form, setForm] = useState({ name: "", name_bn: "", description: "", price: 0, compare_price: 0, stock: 100, image_url: "", flash_sale: false });
  const fileRef = useRef();
  useEffect(() => {
    setForm(editing || { name: "", name_bn: "", description: "", price: 0, compare_price: 0, stock: 100, image_url: "", flash_sale: false });
  }, [editing, open]);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setForm(f => ({ ...f, image_url: r.data.path }));
  };

  const submit = async () => {
    if (!form.name || !form.price) return toast.error("Name & price required");
    try {
      if (editing) await api.put(`/products/${editing.product_id}`, form);
      else await api.post("/products", { ...form, store_id: storeId });
      toast.success("Saved"); onSaved();
    } catch { toast.error("Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} product</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-2">
          <div><Label>Name (English)</Label><Input value={form.name} onChange={e=>setForm({...form, name: e.target.value})} data-testid="p-name"/></div>
          <div><Label>Name (বাংলা)</Label><Input value={form.name_bn} onChange={e=>setForm({...form, name_bn: e.target.value})} className="font-bn"/></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={e=>setForm({...form, description: e.target.value})} rows={2}/></div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label>Price ৳</Label><Input type="number" value={form.price} onChange={e=>setForm({...form, price: parseFloat(e.target.value) || 0})} data-testid="p-price"/></div>
            <div><Label>Compare ৳</Label><Input type="number" value={form.compare_price} onChange={e=>setForm({...form, compare_price: parseFloat(e.target.value) || 0})}/></div>
            <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={e=>setForm({...form, stock: parseInt(e.target.value) || 0})}/></div>
          </div>
          <div>
            <Label>Image</Label>
            <input ref={fileRef} type="file" hidden onChange={e => upload(e.target.files[0])}/>
            <div className="mt-1 flex items-center gap-2">
              {form.image_url && <img src={fileUrl(form.image_url)} className="w-14 h-14 rounded-lg object-cover border" alt=""/>}
              <Button type="button" onClick={() => fileRef.current?.click()} variant="outline"><Upload className="w-4 h-4 mr-1.5"/>Upload</Button>
            </div>
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-sky-50">
            <div className="text-sm font-semibold">Flash Sale</div>
            <Switch checked={form.flash_sale} onCheckedChange={v => setForm({...form, flash_sale: v})}/>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} className="bg-sky-500 hover:bg-sky-600" data-testid="p-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
